import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/apiClient';
import { needsTermsConsent } from '../../utils/termsConsent';

interface FeatureBasedRouteProps {
  children: React.ReactNode;
  requiredFeature: string;
  fallbackPath?: string;
}

/**
 * Route component that checks if a feature is enabled for the current user type.
 * If the feature is disabled, shows an access denied message or redirects.
 */
const FeatureBasedRoute: React.FC<FeatureBasedRouteProps> = ({ 
  children, 
  requiredFeature,
  fallbackPath = '/dashboard',
}) => {
  const { isAuthenticated, isLoading, user, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);
  const [checkingFeature, setCheckingFeature] = useState(true);

  useEffect(() => {
    const checkFeatureAccess = async () => {
      if (!user?.user_type) {
        setCheckingFeature(false);
        return;
      }

      // Superadmin always has access
      if (isSuperAdmin) {
        setFeatureEnabled(true);
        setCheckingFeature(false);
        return;
      }

      if (needsTermsConsent(user)) {
        setCheckingFeature(false);
        return;
      }

      try {
        const response = await api.get<{ user_type: string; features: Record<string, boolean> }>(
          `/auth/admin/feature-matrix/user/${user.user_type}`
        );
        setFeatureEnabled(response.features[requiredFeature] ?? false);
      } catch (error) {
        console.error('Failed to check feature access:', error);
        // Default to false if we can't check
        setFeatureEnabled(false);
      } finally {
        setCheckingFeature(false);
      }
    };

    if (isAuthenticated && user) {
      checkFeatureAccess();
    } else {
      setCheckingFeature(false);
    }
  }, [isAuthenticated, user, isSuperAdmin, requiredFeature]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isSuperAdmin && needsTermsConsent(user)) {
    return <Navigate to="/accept-terms" state={{ from: location }} replace />;
  }

  if (checkingFeature) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  if (featureEnabled === false) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Result
          status="403"
          icon={<LockOutlined style={{ color: '#ff4d4f' }} />}
          title="Feature Not Available"
          subTitle="This feature is not enabled for your account type. Please contact your administrator if you believe this is an error."
          extra={
            <Button type="primary" href={fallbackPath}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default FeatureBasedRoute;



